/** Client-side live clock state derived from Statistics match time fields. */
export interface LiveClockState {
  period: string;
  currentPart: string;
  /** Elapsed seconds in the current period when the anchor was set. */
  elapsedSeconds: number;
  /** When true, the UI ticks locally between server updates. */
  tickable: boolean;
  /** Non-ticking fallback time label, e.g. `67'`. */
  staticTime?: string;
  /** Monotonic anchor used for client-side second ticks. */
  anchoredAt: number;
  /** Changes whenever the server time/period changes so ticks reset. */
  revision: string;
}

export function parseMatchTimeExtended(
  value: unknown,
): { minutes: number; seconds: number } | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    const total = Math.max(0, Math.floor(value));
    return { minutes: Math.floor(total / 60), seconds: total % 60 };
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const colonMatch = /^(\d+):(\d{1,2})$/.exec(raw);
  if (colonMatch) {
    return { minutes: Number(colonMatch[1]), seconds: Number(colonMatch[2]) };
  }

  const apostropheMatch = /^(\d+)['′](\d{1,2})/.exec(raw);
  if (apostropheMatch) {
    return { minutes: Number(apostropheMatch[1]), seconds: Number(apostropheMatch[2]) };
  }

  return null;
}

function isHalfTimePart(currentPart: string): boolean {
  return currentPart === 'HalfTime' || currentPart === 'PAUSED' || currentPart === 'HT';
}

function formatTickingTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatStaticMinutesTime(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.endsWith("'") ? trimmed : `${trimmed}'`;
}

export function resolveLiveClockState(input: {
  period: string;
  currentPart: string;
  time: string;
  timeExtended: string;
  anchoredAt?: number;
}): LiveClockState | undefined {
  const { period, currentPart, time, timeExtended } = input;
  const anchoredAt = input.anchoredAt ?? Date.now();

  if (currentPart && isHalfTimePart(currentPart)) {
    return {
      period,
      currentPart,
      elapsedSeconds: 0,
      tickable: false,
      staticTime: 'HT',
      anchoredAt,
      revision: `ht|${period}|${currentPart}`,
    };
  }

  const extended = parseMatchTimeExtended(timeExtended);
  if (extended) {
    const elapsedSeconds = extended.minutes * 60 + extended.seconds;
    return {
      period,
      currentPart,
      elapsedSeconds,
      tickable: true,
      anchoredAt,
      revision: `ext|${timeExtended}|${period}|${currentPart}`,
    };
  }

  if (time) {
    return {
      period,
      currentPart,
      elapsedSeconds: 0,
      tickable: false,
      staticTime: formatStaticMinutesTime(time),
      anchoredAt,
      revision: `static|${time}|${period}|${currentPart}`,
    };
  }

  if (period) {
    return {
      period,
      currentPart,
      elapsedSeconds: 0,
      tickable: false,
      anchoredAt,
      revision: `period|${period}|${currentPart}`,
    };
  }

  if (currentPart) {
    return {
      period: '',
      currentPart,
      elapsedSeconds: 0,
      tickable: false,
      staticTime: currentPart,
      anchoredAt,
      revision: `part|${currentPart}`,
    };
  }

  return undefined;
}

export function formatLiveClockDisplay(
  state: LiveClockState,
  nowMs: number,
  translatePeriod?: (key: string) => string,
): string | undefined {
  const parts = formatLiveClockParts(state, nowMs, translatePeriod);
  if (!parts) return undefined;
  if (parts.period && parts.time) return `${parts.period} ${parts.time}`;
  return parts.period || parts.time || undefined;
}

/** Split period and time labels for stacked desktop layout. */
export function formatLiveClockParts(
  state: LiveClockState,
  nowMs: number,
  translatePeriod?: (key: string) => string,
): { period?: string; time?: string } | undefined {
  const tr = translatePeriod ?? ((value: string) => value);

  if (state.staticTime === 'HT' || (state.currentPart && isHalfTimePart(state.currentPart))) {
    return { period: tr('HT') };
  }

  const period = state.period ? tr(state.period) : undefined;
  const time = state.tickable
    ? formatTickingTime(
        state.elapsedSeconds + Math.max(0, Math.floor((nowMs - state.anchoredAt) / 1000)),
      )
    : state.staticTime
      ? formatStaticMinutesTime(state.staticTime)
      : !period && state.currentPart
        ? tr(state.currentPart)
        : undefined;

  if (!period && !time) return undefined;
  return { period, time };
}
